// components/Admin/AdminLayout.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { 
  LayoutDashboard, Newspaper, GraduationCap, Users, 
  BarChart3, Settings, Shield, BookOpen, Home, LogOut, Loader,
  ChevronLeft, ChevronRight, Menu, X, Bell, Search, Wrench, Car,
  FileText // ✅ Nouvel icône pour PDFs
} from 'lucide-react';
import NotificationPanel from './NotificationPanel';
import { useNotifications } from '../../hooks/useNotifications';

const AdminLayout = () => {
  const navigate = useNavigate();
  const { userRole, user } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  
  // Hook pour récupérer le nombre de notifications non lues
  const unreadCount = useNotifications(user?.id);

  // Menu items selon le rôle
  const menuItems = [
    {
      title: 'Dashboard',
      path: '/admin',
      icon: LayoutDashboard,
      roles: ['super_admin', 'admin', 'formateur'],
      color: 'blue'
    },
    {
      title: 'Articles',
      path: '/admin/articles',
      icon: Newspaper,
      roles: ['super_admin', 'admin', 'formateur'],
      color: 'cyan'
    },
    // ✅ NOUVEAU : Actualités CFA - accessible par editrice
    {
      title: 'Actualités CFA',
      path: '/admin/actu-cfa',
      icon: Newspaper,
      roles: ['super_admin', 'admin', 'formateur', 'editrice'],
      color: 'teal'
    },
    {
      title: 'Compétences',
      path: '/admin/competences',
      icon: BookOpen,
      roles: ['super_admin', 'admin'],
      color: 'purple'
    },
    {
      title: 'Formations',
      path: '/admin/formations',
      icon: GraduationCap,
      roles: ['super_admin', 'admin', 'formateur'],
      color: 'green'
    },
    {
      title: 'Apprenants',
      path: '/admin/apprenants',
      icon: Users,
      roles: ['super_admin', 'admin', 'formateur'],
      color: 'orange'
    },
    {
      title: 'Statistiques',
      path: '/admin/statistiques',
      icon: BarChart3,
      roles: ['super_admin', 'admin'],
      color: 'pink'
    },
    {
      title: 'Interventions',
      path: '/admin/interventions-pedagogiques',
      icon: Wrench,
      roles: ['super_admin', 'admin', 'formateur'],
      color: 'emerald'
    },
    {
      title: 'Clients Auto',
      path: '/admin/statistiques-clients',
      icon: Car,
      roles: ['super_admin', 'admin', 'formateur'],
      color: 'violet'
    },
    // ✅ NOUVEAU : Page PDFs
    {
      title: 'Documents PDF',
      path: '/admin/pdfs',
      icon: FileText,
      roles: ['super_admin', 'admin', 'formateur'],
      color: 'red'
    },
    {
      title: 'Rôles',
      path: '/admin/roles',
      icon: Shield,
      roles: ['super_admin'],
      color: 'indigo'
    },
    {
      title: 'Paramètres',
      path: '/admin/parametres',
      icon: Settings,
      roles: ['super_admin', 'admin'],
      color: 'gray'
    }
  ];

  // Filtrer selon le rôle
  const accessibleMenuItems = menuItems.filter(item => 
    item.roles.includes(userRole)
  );

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setLoggingOut(false);
    }
  };

  const getColorClasses = (color, isActive) => {
    const colors = {
      blue: isActive ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-blue-600/10 hover:text-blue-400',
      cyan: isActive ? 'bg-cyan-600 text-white' : 'text-gray-300 hover:bg-cyan-600/10 hover:text-cyan-400',
      teal: isActive ? 'bg-teal-600 text-white' : 'text-gray-300 hover:bg-teal-600/10 hover:text-teal-400', // ✅ Nouvelle couleur pour Actualités CFA
      purple: isActive ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-purple-600/10 hover:text-purple-400',
      green: isActive ? 'bg-green-600 text-white' : 'text-gray-300 hover:bg-green-600/10 hover:text-green-400',
      orange: isActive ? 'bg-orange-600 text-white' : 'text-gray-300 hover:bg-orange-600/10 hover:text-orange-400',
      pink: isActive ? 'bg-pink-600 text-white' : 'text-gray-300 hover:bg-pink-600/10 hover:text-pink-400',
      indigo: isActive ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:bg-indigo-600/10 hover:text-indigo-400',
      gray: isActive ? 'bg-gray-600 text-white' : 'text-gray-300 hover:bg-gray-600/10 hover:text-gray-400',
      emerald: isActive ? 'bg-emerald-600 text-white' : 'text-gray-300 hover:bg-emerald-600/10 hover:text-emerald-400',
      violet: isActive ? 'bg-violet-600 text-white' : 'text-gray-300 hover:bg-violet-600/10 hover:text-violet-400',
      red: isActive ? 'bg-red-600 text-white' : 'text-gray-300 hover:bg-red-600/10 hover:text-red-400',
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Sidebar Desktop */}
      <motion.aside
        initial={false}
        animate={{ 
          width: isSidebarOpen ? '280px' : '80px',
        }}
        className="hidden md:flex fixed left-0 top-0 h-screen bg-slate-800/50 backdrop-blur-xl border-r border-slate-700 flex-col z-40"
      >
        {/* Header Sidebar */}
        <div className="pt-32 px-8 pb-8 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <AnimatePresence mode="wait">
              {isSidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex items-center gap-3"
                >
                  <motion.div 
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                    animate={{ 
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{ 
                      rotate: { repeat: Infinity, duration: 3, ease: "easeInOut" }
                    }}
                    className="bg-gradient-to-br from-blue-600 to-purple-700 p-2.5 rounded-xl shadow-lg"
                  >
                    <Shield className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <h1 className="text-lg font-bold text-white">Admin</h1>
                    <p className="text-xs text-gray-400 capitalize">
                      {userRole === 'editrice' ? 'Éditrice' : userRole}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <motion.div
                animate={{ rotate: isSidebarOpen ? 0 : 180 }}
                transition={{ duration: 0.3 }}
              >
                {isSidebarOpen ? (
                  <ChevronLeft className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {accessibleMenuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={index}
                to={item.path}
                end={item.path === '/admin'}
                className="block"
              >
                {({ isActive }) => (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ 
                      scale: 1.03,
                      x: 5,
                    }}
                    whileTap={{ scale: 0.97 }}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-xl transition-all cursor-pointer
                      ${getColorClasses(item.color, isActive)}
                      ${isActive ? 'shadow-lg' : ''}
                    `}
                  >
                    <motion.div
                      whileHover={{ 
                        rotate: 360,
                        transition: { duration: 0.6, type: "spring" }
                      }}
                      whileTap={{ scale: 0.9 }}
                      animate={isActive ? {
                        scale: [1, 1.1, 1],
                        transition: { repeat: Infinity, duration: 2 }
                      } : {}}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                    </motion.div>
                    
                    <AnimatePresence mode="wait">
                      {isSidebarOpen && (
                        <motion.span
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: "auto" }}
                          exit={{ opacity: 0, width: 0 }}
                          className="font-medium text-sm whitespace-nowrap overflow-hidden"
                        >
                          {item.title}
                        </motion.span>
                      )}
                    </AnimatePresence>

                    {isActive && isSidebarOpen && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="ml-auto w-1.5 h-1.5 bg-white rounded-full"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                  </motion.div>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer Sidebar */}
        <div className="p-4 border-t border-slate-700 space-y-2">
          <motion.button
            whileHover={{ scale: 1.02, x: 5 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/')}
            className={`
              w-full flex items-center gap-3 px-4 py-3 rounded-xl 
              text-gray-300 hover:bg-slate-700 hover:text-white transition-all
              ${!isSidebarOpen && 'justify-center'}
            `}
          >
            <motion.div 
              whileHover={{ 
                rotate: [0, -10, 10, -10, 0],
                transition: { duration: 0.5 }
              }}
            >
              <Home className="w-5 h-5 flex-shrink-0" />
            </motion.div>
            <AnimatePresence mode="wait">
              {isSidebarOpen && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  className="font-medium text-sm whitespace-nowrap overflow-hidden"
                >
                  Accueil
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02, x: 5 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleLogout}
            disabled={loggingOut}
            className={`
              w-full flex items-center gap-3 px-4 py-3 rounded-xl 
              text-gray-300 hover:bg-red-600/10 hover:text-red-400 transition-all
              disabled:opacity-50
              ${!isSidebarOpen && 'justify-center'}
            `}
          >
            {loggingOut ? (
              <Loader className="w-5 h-5 flex-shrink-0 animate-spin" />
            ) : (
              <motion.div 
                whileHover={{ 
                  rotate: 180,
                  scale: 1.1,
                  transition: { duration: 0.4 }
                }}
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
              </motion.div>
            )}
            <AnimatePresence mode="wait">
              {isSidebarOpen && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  className="font-medium text-sm whitespace-nowrap overflow-hidden"
                >
                  {loggingOut ? 'Déconnexion...' : 'Déconnexion'}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </motion.aside>

      {/* Bouton menu mobile */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-3 bg-slate-800 rounded-xl shadow-lg text-white"
      >
        <AnimatePresence mode="wait">
          {isMobileMenuOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
            >
              <Menu className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Menu Mobile */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="md:hidden fixed left-0 top-0 h-screen w-80 bg-slate-800/95 backdrop-blur-xl border-r border-slate-700 z-40 flex flex-col"
            >
              {/* Mobile Header */}
              <div className="pt-24 px-6 pb-6 border-b border-slate-700">
                <div className="flex items-center gap-3">
                  <motion.div 
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    animate={{ 
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{ 
                      rotate: { repeat: Infinity, duration: 3, ease: "easeInOut" }
                    }}
                    className="bg-gradient-to-br from-blue-600 to-purple-700 p-2.5 rounded-xl shadow-lg"
                  >
                    <Shield className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <h1 className="text-lg font-bold text-white">Admin</h1>
                    <p className="text-xs text-gray-400 capitalize">
                      {userRole === 'editrice' ? 'Éditrice' : userRole}
                    </p>
                  </div>
                </div>
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                {accessibleMenuItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={index}
                      to={item.path}
                      end={item.path === '/admin'}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block"
                    >
                      {({ isActive }) => (
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ scale: 1.03, x: 5 }}
                          whileTap={{ scale: 0.97 }}
                          className={`
                            flex items-center gap-3 px-4 py-3 rounded-xl transition-all cursor-pointer
                            ${getColorClasses(item.color, isActive)}
                            ${isActive ? 'shadow-lg' : ''}
                          `}
                        >
                          <motion.div
                            whileHover={{ 
                              rotate: 360,
                              transition: { duration: 0.6, type: "spring" }
                            }}
                            whileTap={{ scale: 0.9 }}
                            animate={isActive ? {
                              scale: [1, 1.1, 1],
                              transition: { repeat: Infinity, duration: 2 }
                            } : {}}
                          >
                            <Icon className="w-5 h-5 flex-shrink-0" />
                          </motion.div>
                          <span className="font-medium text-left">{item.title}</span>
                          {isActive && (
                            <motion.div
                              layoutId="mobileActiveIndicator"
                              className="ml-auto w-1.5 h-1.5 bg-white rounded-full"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                            />
                          )}
                        </motion.div>
                      )}
                    </NavLink>
                  );
                })}
              </nav>

              {/* Mobile Footer */}
              <div className="p-4 border-t border-slate-700 space-y-2">
                <motion.button
                  whileHover={{ scale: 1.02, x: 5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    navigate('/');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-slate-700 hover:text-white transition-all"
                >
                  <motion.div 
                    whileHover={{ 
                      rotate: [0, -10, 10, -10, 0],
                      transition: { duration: 0.5 }
                    }}
                  >
                    <Home className="w-5 h-5 flex-shrink-0" />
                  </motion.div>
                  <span className="font-medium">Accueil</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02, x: 5 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-red-600/10 hover:text-red-400 transition-all disabled:opacity-50"
                >
                  {loggingOut ? (
                    <Loader className="w-5 h-5 flex-shrink-0 animate-spin" />
                  ) : (
                    <motion.div 
                      whileHover={{ 
                        rotate: 180,
                        scale: 1.1,
                        transition: { duration: 0.4 }
                      }}
                    >
                      <LogOut className="w-5 h-5 flex-shrink-0" />
                    </motion.div>
                  )}
                  <span className="font-medium">
                    {loggingOut ? 'Déconnexion...' : 'Déconnexion'}
                  </span>
                </motion.button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.main
        animate={{
          marginLeft: isSidebarOpen ? '280px' : '80px',
        }}
        className="min-h-screen transition-all duration-300 md:ml-0"
      >
        {/* Top Bar */}
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-slate-800/30 backdrop-blur-xl border-b border-slate-700 sticky top-0 z-30"
        >
          <div className="px-6 md:px-8 pt-32 pb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-0 mb-4 md:mb-6">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-white mb-1">
                  Administration
                </h1>
                <p className="text-xs md:text-sm text-gray-400">
                  {new Date().toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              
              <div className="flex items-center gap-3 md:gap-4">
                <motion.button 
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: [0, -15, 15, -15, 0],
                    transition: { rotate: { duration: 0.5 } }
                  }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsNotificationPanelOpen(true)}
                  className="relative p-2.5 md:p-3 text-gray-400 hover:text-white hover:bg-slate-700 rounded-xl transition-colors"
                >
                  <motion.div
                    animate={{
                      rotate: unreadCount > 0 ? [0, 10, -10, 0] : 0
                    }}
                    transition={{
                      repeat: unreadCount > 0 ? Infinity : 0,
                      duration: 2,
                      ease: "easeInOut"
                    }}
                  >
                    <Bell className="w-4 h-4 md:w-5 md:h-5" />
                  </motion.div>
                  {unreadCount > 0 && (
                    <motion.span 
                      initial={{ scale: 0 }}
                      animate={{ 
                        scale: 1,
                      }}
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </motion.span>
                  )}
                </motion.button>
                
                <div className="flex items-center gap-3 md:gap-4 pl-3 md:pl-4 border-l border-slate-700">
                  <div className="text-right hidden md:block">
                    <p className="text-xs md:text-sm font-semibold text-white">{user?.email?.split('@')[0]}</p>
                    <p className="text-xs text-gray-400 capitalize">
                      {userRole === 'editrice' ? 'Éditrice' : userRole}
                    </p>
                  </div>
                  <motion.div 
                    whileHover={{ 
                      scale: 1.1, 
                      rotate: 5,
                      boxShadow: "0 0 20px rgba(59, 130, 246, 0.5)"
                    }}
                    whileTap={{ scale: 0.95 }}
                    animate={{
                      rotate: [0, 3, -3, 0]
                    }}
                    transition={{
                      rotate: { repeat: Infinity, duration: 3, ease: "easeInOut" }
                    }}
                    className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-blue-600 to-purple-700 rounded-lg flex items-center justify-center shadow-lg cursor-pointer"
                  >
                    <Shield className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Barre de recherche */}
            <div className="max-w-full md:max-w-2xl">
              <div className="relative">
                <motion.div
                  animate={{
                    x: [0, 2, -2, 0]
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 2,
                    ease: "easeInOut"
                  }}
                  className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  <Search className="w-4 h-4" />
                </motion.div>
                <motion.input
                  whileFocus={{ scale: 1.01 }}
                  type="text"
                  placeholder="Rechercher dans l'administration..."
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2.5 md:py-3 pl-10 md:pl-12 pr-4 text-xs md:text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Contenu principal */}
        <div className="pt-6 md:pt-8 pb-8 md:pb-12">
          <Outlet />
        </div>
      </motion.main>

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
        userId={user?.id}
      />
    </div>
  );
};

export default AdminLayout;